// ArticlesDataProvider.tsx
import React, { createContext, useContext, useMemo } from "react";
import { InfiniteData } from "@tanstack/react-query";
import { useNewsArticles } from "../features/news-feed/data/useNewsArticles";
import { Article } from "../shared/types/DataTypes";

interface ArticlesDataContextValue {
  articlesData: InfiniteData<any, unknown> | undefined;
  articles: Article[];
  isLoading: boolean;
  isFetched: boolean;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  fetchNextPage: () => Promise<any>;
  fetchPreviousPage: () => Promise<any>;
  refetch: () => Promise<any>;
  error: Error | null;
}

const ArticlesDataContext =
  createContext<ArticlesDataContextValue | null>(null);

export function ArticlesDataProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    data,
    isLoading,
    isFetched,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    refetch,
    error,
  } = useNewsArticles();

  const articles = useMemo(
    () => data?.pages.flatMap((p) => p.articles) ?? [],
    [data]
  );

  const value = useMemo(
    () => ({
      articlesData: data,
      articles,
      isLoading,
      isFetched,
      isFetchingNextPage,
      isFetchingPreviousPage,
      hasNextPage,
      hasPreviousPage,
      fetchNextPage,
      fetchPreviousPage,
      refetch,
      error,
    }),
    [
      data,
      articles,
      isLoading,
      isFetched,
      isFetchingNextPage,
      isFetchingPreviousPage,
      hasNextPage,
      hasPreviousPage,
      fetchNextPage,
      fetchPreviousPage,
      refetch,
      error,
    ]
  );

  return (
    <ArticlesDataContext.Provider value={value}>
      {children}
    </ArticlesDataContext.Provider>
  );
}

export function useArticlesData() {
  const ctx = useContext(ArticlesDataContext);
  if (!ctx) {
    throw new Error("useArticlesData must be used within ArticlesDataProvider");
  }
  return ctx;
}
